import { HttpErrorResponse } from '@angular/common/http';

import { catchError, from, fromEvent, mergeMap, Observable, of, take, tap } from 'rxjs';

import { Environment } from '@app/shared/models/environment';
import { RestError } from '@azure/storage-blob';
import { fromByteArray } from 'base64-js';

import { MEGABYTE_1 } from '../../../../apps/admin-app/src/constants';
import { BlobContainer, ErrorCodes, UserRoles } from './enums';

export type ProgressFn = (event: ProgressEvent, isComplete: boolean) => void;
export type UploadFn = (file: File, progressFn?: ProgressFn) => Promise<string>;

export const LOADING_SPINNER =
    'data:image/gif;base64,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';

export function replaceIllegalCharacters(name: string): string {
    return name.replace(/[^\x00-\x7F]/g, '').replace(/[<>%&:\\?/+*']/g, '');
}

export function readAsText(file: File | Blob): Observable<string> {
    return new Observable(subscriber => {
        const reader = new FileReader();
        reader.onerror = err => {
            subscriber.error(err);
            subscriber.complete();
        };
        reader.onload = () => {
            subscriber.next(reader.result as string);
            subscriber.complete();
        };
        reader.readAsText(file);
    });
}

export function base64ToBlob(data: any, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
}

export function parseJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(''),
    );

    return JSON.parse(jsonPayload);
}

export async function handleFileUpload(
    serverUrl: string,
    token: string,
    file: File,
    progressFn?: (e: ProgressEvent, isComplete: boolean) => void,
    isPublic = false,
): Promise<string> {
    return new Promise((resolve, reject) => {
        if (file === null) {
            return reject();
        }

        const { type, size } = file;

        const reader: FileReader = new FileReader();

        reader.onerror = err => reject(err);

        fromEvent(reader, 'loadend')
            .pipe(
                take(1),
                mergeMap(() => {
                    const blob = new Blob([reader.result as ArrayBuffer], { type });
                    const uri = URL.createObjectURL(blob);

                    return of(uri);
                }),
                mergeMap(src =>
                    from(
                        uploadFile(
                            serverUrl,
                            src,
                            token,
                            file.name,
                            type === 'video/quicktime' ? 'application/octet-stream; charset=UTF-8' : null,
                            progressFn,
                            isPublic,
                        ),
                    ),
                ),
                tap(data => resolve(data)),
                catchError(err => {
                    reject(err);
                    return of(null);
                }),
            )
            .subscribe();

        reader.readAsArrayBuffer(file);
    });
}

export async function prepareImageForUpload(file: File, compressThreshold: boolean | number = MEGABYTE_1): Promise<string> {
    return new Promise((resolve, reject) => {
        if (file === null) {
            return reject();
        }

        const { type, size } = file;
        const reader: FileReader = new FileReader();

        reader.onerror = err => reject(err);

        fromEvent(reader, 'loadend')
            .pipe(
                take(1),
                mergeMap(() => {
                    const base64 = fromByteArray(new Uint8Array(reader.result as ArrayBuffer));
                    const src = `data:${type};base64,${base64}`;

                    if (compressThreshold === false || (isNumber(compressThreshold) && size < compressThreshold)) {
                        return of(src);
                    }

                    const img = new Image();
                    img.src = src;

                    return fromEvent(img, 'load').pipe(
                        take(1),
                        mergeMap(() => {
                            return from(compressImage(img, 0.3, img.width, img.height));
                        }),
                    );
                }),
                tap(data => resolve(data)),
                catchError(err => {
                    reject(err);
                    return of(null);
                }),
            )
            .subscribe();

        reader.readAsArrayBuffer(file);
    });
}

export function compressImage(image: HTMLImageElement, quality: number, width: number, height: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(image, 0, 0, width, height);

        canvas.toBlob(
            blob => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        const base64data = reader.result as string;
                        resolve(base64data);
                        // this.editor.chain().setImage({ src: base64data }).run();
                    };
                    return;
                }

                reject();
            },
            'image/jpeg',
            quality,
        );
    });
}

export async function uploadFile(
    serverUrl: string,
    src: string,
    token: string,
    filename?: string,
    contentType: string | null = null,
    progressFn?: ProgressFn,
    isPublic = false,
): Promise<string> {
    const result = await fetch(src);

    const type = contentType || (result.headers.get('Content-Type') as string);
    const file = new File([await result.arrayBuffer()], filename || 'file', { type });

    const formData = new FormData();
    formData.append('isPublic', isPublic.toString());
    formData.append('data', file);
    formData.append('contentType', type);

    const response = await makeRequest('POST', `${serverUrl}/file/public/upload`, formData, token, progressFn);

    try {
        const json = JSON.parse(response as string);
        return json.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

export function makeRequest(method: string, url: string, body: any, token: string, progressFn?: ProgressFn) {
    return new Promise(function (resolve, reject) {
        const xhr = new XMLHttpRequest();

        xhr.open(method, url);
        xhr.onload = function () {
            if (this.status >= 200 && this.status < 300) {
                resolve(xhr.response);
            } else {
                reject({
                    status: this.status,
                    statusText: xhr.statusText,
                });
            }
        };
        xhr.onerror = function () {
            reject({
                status: this.status,
                statusText: xhr.statusText,
            });
        };

        xhr.upload.addEventListener('progress', e => {
            if (progressFn) {
                progressFn(e, false);
            }
        });

        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.send(body);
    });
}

export function getErrorCodeMessage(code: ErrorCodes, meta?: { [key: string]: string | number | string[] }): string {

    switch (code) {

        // 600
        case ErrorCodes.NoConnection:
            return `Error Code (${code}): Connection could not be established. Please check your internet connection.`;
        case ErrorCodes.NormailizeAudio:
            return `Error Code (${code}): An error has occurred while attempting to process the audio recording. Please try again later.`;
        case ErrorCodes.CookieTestFailed:
            return `Error Code (${code}): Cookies must be enabled to proceed.`;
        default:
            return `Error Code (${code}): An error has occured. Please try again later.`;
    }
}

export function getHttpErrorMessage(response: HttpErrorResponse) {
    switch (response.status) {
        case 504:
            return 'Gateway Timeout Occurred. Please check your connection and try again later.';
        case 0:
            return 'Could not communicate with server. Please check your connection.';
        default:
            return `(${response.status}) ${response.statusText}`;
    }
}

export function getRestErrorMessage(error: RestError): string {
    switch (error.statusCode) {
        case 404:
            return `Error Code: ${error.statusCode} Resource Not Found`;
        default: {
            if (error.details) {
                const details = (error.details as any) || null;
                return `Error Code: ${error.statusCode} ${details?.errorCode}`.trim();
            }
        }
    }

    return error.message;
}

export function getTextContent(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent ?? '';
}

export function hasContent(value: string | null | undefined, isHtml = false): boolean {
    if (!value) {
        return false;
    }

    if (isHtml) {
        const div = document.createElement('div');
        div.innerHTML = value;
        return (div.textContent ?? '').trim().length > 0 || div.querySelector('img,video,audio,media-asset') !== null;
    }

    return !isNullOrEmpty(value);
}

export function isHttpError(model: any): model is HttpErrorResponse {
    //return model instanceof HttpErrorResponse; // doesn't work because it's referenced from a separate npm library
    return model?.constructor?.name === 'HttpErrorResponse';
}

export function isRestError(model: any): model is RestError {
    return model instanceof RestError;
}

export const DEFAULT_TIME_ZONE = new Intl.DateTimeFormat('en-US').resolvedOptions().timeZone;

export function isValidDate(model: Date): boolean {
    return model instanceof Date && !isNaN(model.valueOf());
}

export function isObject(model: any): model is object {
    return getType(model) === 'object';
}

export function isFunction(model: any): model is Function {
    return getType(model) === 'function';
}

export function isError(model: any): model is Error {
    return getType(model) === 'error';
}

export function isString(model: any): model is string {
    return getType(model) === 'string';
}

export function isBoolean(model: any): model is string {
    return getType(model) === 'boolean';
}

export function isFile(model: any): model is File {
    return getType(model) === 'file';
}

export function isArray<T>(model: any): model is Array<T> {
    return getType(model) === 'array';
}

export function isNumber(model: any): model is number {
    return getType(model) === 'number';
}

export function isDate(model: any): model is Date {
    return getType(model) === 'date';
}

export function isUint8Array(model: any): model is Uint8Array {
    return getType(model) === 'uint8array';
}

export function isBlob(model: any): model is Blob {
    return getType(model) === 'blob';
}

export function isMediaError(model: any): model is MediaError {
    return getType(model) === 'mediaerror';
}

export function isNullOrUndefined(value: any): value is null | undefined {
    return value === null || value === undefined;
}

export function isNullOrEmpty(value: string | null | undefined): value is null | undefined | '' {
    return isNullOrUndefined(value) || value === '';
}

export function isType<T>(model: any, condition: string | ((m: any) => boolean)): model is T {
    if (isString(condition)) {
        return Object.prototype.hasOwnProperty.call(model, condition);
    }

    return condition(model);
}

export function getType(model: any): string {
    const type = {}.toString.call(model);

    switch (type) {
        case '[object Number]':
            return 'number';
        case '[object Date]':
            return 'date';
        case '[object String]':
            return 'string';
        case '[object Null]':
            return 'null';
        case '[object Undefined]':
            return 'undefined';
        case '[object Object]':
            return 'object';
        case '[object Boolean]':
            return 'boolean';
        case '[object Array]':
            return 'array';
        case '[object File]':
            return 'file';
        case '[object Uint8Array]':
            return 'uint8array';
        case '[object Blob]':
            return 'blob';
        case '[object Error]':
            return 'error';
        case '[object MediaError]':
            return 'mediaerror';
        case '[object Function]':
            return 'function';
        default:
            return type;
    }
}

export function isSameDate(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function isAdmin(role: UserRoles | null | undefined): boolean {
    return role !== null && role !== undefined && (role & UserRoles.Administrator) !== 0;
}

export function daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

export function getContainerName(env: Environment, containerName: string | BlobContainer = BlobContainer.Private): string {
    if (typeof containerName === 'string') {
        return containerName;
    }

    return containerName === BlobContainer.Private ? env.privateContainerName : env.publicContainerName;
}
